/**
 * Turns an AI proposal (from the ai-draft-quote edge function) into quote
 * editor values. Pure, so tests/ai-quote-draft-lib.spec.ts can check it.
 *
 * Shape mirrors QuoteDraft in supabase/functions/_shared/ai-draft-quote.ts.
 * Nothing is saved here: the editor fills its form and the owner reviews it.
 */
import { defaultValidUntil, roundMoney } from './quotes';

export type AiPriceSource = 'price_list' | 'estimate';

export interface AiDraftLine {
  description: string;
  quantity: number;
  unit_price: number;
  service_id: string | null;
  price_source: AiPriceSource;
  is_optional: boolean;
  note: string;
}

export interface AiQuoteDraft {
  title: string;
  scope_summary: string;
  lines: AiDraftLine[];
  assumptions: string[];
  exclusions: string[];
  valid_for_days: number;
  questions: string[];
}

export interface AiQuoteFormLine {
  description: string;
  service_id: string;
  quantity: number;
  unit_amount: number;
  is_optional: boolean;
  /** True when the price is the AI's estimate rather than from the price list. */
  ai_estimate: boolean;
}

export interface AiQuoteFormValues {
  lines: AiQuoteFormLine[];
  notes: string;
  valid_until: string;
}

/** Notes field limit in the quote editor. */
export const QUOTE_NOTES_MAX = 2000;

const NONE = 'none';

const bullets = (heading: string, items: string[]) =>
  items.length ? `${heading}\n${items.map((item) => `• ${item}`).join('\n')}` : '';

/** Scope, assumptions and exclusions as one customer-facing notes block. */
export function draftNotes(draft: Pick<AiQuoteDraft, 'scope_summary' | 'assumptions' | 'exclusions'>): string {
  return [
    draft.scope_summary.trim(),
    bullets("What we've assumed:", draft.assumptions),
    bullets('Not included:', draft.exclusions),
  ]
    .filter(Boolean)
    .join('\n\n');
}

/**
 * Join existing notes and the draft notes, within the editor's limit. Existing
 * notes come first and are never cut; the draft is trimmed at a line break.
 */
export function mergeNotes(existing: string, addition: string, max = QUOTE_NOTES_MAX): string {
  const base = existing.trim();
  const joined = base ? `${base}\n\n${addition}` : addition;
  if (joined.length <= max) return joined;
  const room = max - (base ? base.length + 2 : 0);
  if (room <= 0) return base.slice(0, max);
  const cut = addition.slice(0, room);
  const lastBreak = cut.lastIndexOf('\n');
  const trimmed = (lastBreak > room / 2 ? cut.slice(0, lastBreak) : cut).trimEnd();
  return base ? `${base}\n\n${trimmed}` : trimmed;
}

/** valid_for_days → yyyy-MM-dd from today. */
export function draftValidUntil(days: number, now: Date = new Date()): string {
  const n = Number.isFinite(days) ? Math.min(90, Math.max(1, Math.round(days))) : 30;
  return defaultValidUntil(now, n);
}

/**
 * Lines for the editor. Price-list lines keep their service link; any line
 * the price list didn't price is flagged ai_estimate so the editor marks it.
 * `knownServiceIds` guards against a service that was deleted meanwhile.
 */
export function draftToQuoteLines(draft: Pick<AiQuoteDraft, 'lines'>, knownServiceIds?: Set<string>): AiQuoteFormLine[] {
  return draft.lines
    .filter((line) => line.description.trim())
    .map((line) => {
      const serviceKnown = Boolean(line.service_id && (!knownServiceIds || knownServiceIds.has(line.service_id)));
      const quantity = Number(line.quantity);
      const unit = Number(line.unit_price);
      return {
        description: line.description.trim().slice(0, 200),
        service_id: serviceKnown ? (line.service_id as string) : NONE,
        quantity: Number.isFinite(quantity) && quantity > 0 ? roundMoney(quantity) : 1,
        unit_amount: Number.isFinite(unit) && unit >= 0 ? roundMoney(unit) : 0,
        is_optional: Boolean(line.is_optional),
        ai_estimate: line.price_source !== 'price_list' || !serviceKnown,
      };
    });
}

export function draftToQuoteForm(
  draft: AiQuoteDraft,
  options: { existingNotes?: string; knownServiceIds?: Set<string>; now?: Date } = {},
): AiQuoteFormValues {
  return {
    lines: draftToQuoteLines(draft, options.knownServiceIds),
    notes: mergeNotes(options.existingNotes ?? '', draftNotes(draft)),
    valid_until: draftValidUntil(draft.valid_for_days, options.now),
  };
}

/** True when the editor only has the untouched starter line. */
export function isBlankQuoteLines(lines: Array<{ description?: string; unit_amount?: number | string }>): boolean {
  return lines.every((line) => !String(line.description ?? '').trim() && !Number(line.unit_amount ?? 0));
}
